import { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { landingPagesData, type LandingPageData } from '../data/landingPages';
import { trackEvent, trackConversion } from '../lib/analytics';
import { usePageMeta } from '../lib/usePageMeta';
import { useJsonLd } from '../lib/useJsonLd';
import { getAttributionParams } from '../lib/attribution';

const WHATSAPP_NUMBER = '5511969241378';
const PHONE_DISPLAY = '(11) 2369-0371';
const PHONE_TEL = '+551123690371';
const WHATSAPP_DISPLAY = '(11) 96924-1378';

/**
 * Landing page de anúncio (/lp/:slug).
 *
 * Diferente das páginas do site: sem menu de navegação (caminho único até a
 * conversão), CTA acima da dobra, um único serviço por página e `noindex`
 * (existe apenas como destino de tráfego pago).
 */
export const AdLandingPage = () => {
    const { slug } = useParams();
    const data = slug ? landingPagesData[slug] : null;

    if (!data) {
        return <Navigate to="/" replace />;
    }

    return <AdLanding data={data} />;
};

const AdLanding = ({ data }: { data: LandingPageData }) => {
    usePageMeta({
        title: data.seo.title,
        description: data.seo.description,
        // Página de anúncio: fora do índice de busca (evita conteúdo duplicado
        // com as páginas institucionais em /areas/*). O robots.txt NÃO bloqueia
        // /lp/ de propósito — bloquear impediria o crawler de ler este noindex
        // e o AdsBot de validar o destino do anúncio.
        robots: 'noindex, nofollow',
        canonical: `https://paccesadvogados.com.br/lp/${data.slug}`,
        image: data.seo.ogImage,
    });

    /**
     * FAQPage — as mesmas perguntas que já estão visíveis na página, exigência
     * do Google para o schema ser válido.
     *
     * Não emitimos LegalService aqui: o index.html já serve um, em todas as
     * rotas. Dois nós LegalService no mesmo documento fazem o Google escolher
     * um deles sem critério — pior do que ter só o global.
     *
     * ⚠ Esta página é `noindex`. Rich result de FAQ ela nunca vai render — só
     * página indexada rende. O schema fica porque descreve corretamente o
     * documento e o AdsBot lê a página, não porque vai virar estrelinha na SERP.
     */
    useJsonLd(
        [
            {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: data.faq.items.map((item) => ({
                    '@type': 'Question',
                    name: item.question,
                    acceptedAnswer: { '@type': 'Answer', text: item.answer },
                })),
            },
        ],
        `lp-${data.slug}`,
    );

    return (
        // `[&_p]:text-left` etc. neutralizam o `text-align: justify` global do
        // index.css (`p, li, address`), que abre rios de espaço em texto curto.
        <div className="bg-white text-stone-900 font-sans [&_p]:text-left [&_li]:text-left [&_address]:text-left">
            <Hero data={data} />
            <Stats data={data} />
            {data.prose?.map((bloco) => <Prose key={bloco.title} prose={bloco} />)}
            {data.steps && <Steps steps={data.steps} />}
            <MidCta data={data} />
            {data.checklist && <Checklist checklist={data.checklist} />}
            <Faq faq={data.faq} />
            <ContactSection data={data} />
            <MinimalFooter />
        </div>
    );
};

/* ─────────────────────────── Hero ─────────────────────────── */

/**
 * O hero NÃO tem animação de entrada — de propósito.
 *
 * Ele já teve: um fade-up com framer-motion (`initial: {opacity: 0}`). O efeito
 * dependia do requestAnimationFrame, que o navegador CONGELA em aba oculta ou
 * sem foco. Nesse caso a animação nunca rodava e o H1 e os botões ficavam presos
 * em `opacity: 0` — hero em branco. Cenário real em tráfego pago: a pessoa clica
 * no anúncio no celular, troca de app enquanto carrega e volta para uma tela sem
 * título e sem botão.
 *
 * Regra que vale daqui pra frente nesta página: o conteúdo é visível por padrão;
 * animação só pode ENRIQUECER algo que já aparece sem JavaScript.
 */
const Hero = ({ data }: { data: LandingPageData }) => {
    return (
        <header className="relative bg-pacces-ebony text-white overflow-hidden">
            {/* Imagem de fundo com máscara — o texto fica sobre a área escura */}
            <div className="absolute inset-0 z-0">
                <img
                    src={data.hero.image}
                    alt={data.hero.imageAlt}
                    className="w-full h-full object-cover opacity-40 lg:opacity-100 lg:object-right"
                    fetchPriority="high"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-pacces-ebony via-pacces-ebony/95 to-pacces-ebony/60 lg:to-transparent" />
                <div className="absolute inset-0 bg-pacces-ebony/60 lg:hidden" />
            </div>

            <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-16 sm:pt-14 sm:pb-24">
                {/* Marca — sem navegação: a LP tem caminho único */}
                <img
                    src="/logo/Logo-Pacces-Advogados-Branco.png"
                    alt="Pacces Advogados"
                    className="h-11 sm:h-12 w-auto mb-7 sm:mb-10"
                />

                <div className="max-w-2xl">
                    <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.3em] text-pacces-gold mb-5">
                        {data.hero.kicker}
                    </p>

                    <h1 className="font-serif text-[2rem] leading-[1.05] sm:text-5xl lg:text-6xl tracking-[-0.02em] text-white text-balance">
                        {data.hero.h1}
                    </h1>

                    <p className="mt-5 text-lg sm:text-2xl text-pacces-gold font-serif italic text-balance">
                        {data.hero.subtitle}
                    </p>

                    <div className="mt-7 space-y-4 max-w-xl">
                        {data.hero.lead.map((p, i) => (
                            <p key={i} className="text-base sm:text-lg text-stone-300 leading-relaxed">
                                {p}
                            </p>
                        ))}
                    </div>

                    <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <WhatsAppCta
                            message={data.whatsappMessage}
                            label="Falar com um Especialista"
                            variant="primary"
                        />
                        <a
                            href="#contato"
                            className="inline-flex items-center justify-center gap-2 border border-stone-600 text-white px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:border-pacces-gold hover:text-pacces-gold transition-colors min-h-[52px]"
                        >
                            Solicitar Análise
                        </a>
                    </div>

                    <p className="mt-6 text-sm text-stone-400">
                        Atendimento confidencial · Escritório em São Paulo
                    </p>
                </div>
            </div>
        </header>
    );
};

/* ─────────────────────────── Stats ─────────────────────────── */

const Stats = ({ data }: { data: LandingPageData }) => (
    <section className="bg-stone-50 border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-12">
            <ul className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10">
                {data.stats.map((s) => (
                    <li key={s.label}>
                        <span className="block font-serif text-3xl sm:text-4xl text-stone-900 leading-none">
                            {s.value}
                        </span>
                        <span className="mt-2 block text-[11px] sm:text-xs font-bold uppercase tracking-[0.15em] text-stone-600">
                            {s.label}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

/* ─────────────────────── Texto corrido ─────────────────────── */

const Prose = ({ prose }: { prose: NonNullable<LandingPageData['prose']>[number] }) => (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24 [&+&]:pt-0">
        <div className="max-w-3xl">
            <SectionTitle>{prose.title}</SectionTitle>
            <div className="mt-8 space-y-6">
                {prose.paragraphs.map((p, i) => (
                    <p key={i} className="text-base sm:text-lg text-stone-700 leading-relaxed text-pretty">
                        {p}
                    </p>
                ))}
            </div>
        </div>
    </section>
);

/* ───────────────────────── Etapas ─────────────────────────── */

const Steps = ({ steps }: { steps: NonNullable<LandingPageData['steps']> }) => (
    <section className="bg-stone-50 border-y border-stone-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
            <div className="max-w-3xl">
                <SectionTitle>{steps.title}</SectionTitle>
                {steps.subtitle && (
                    <p className="mt-3 font-serif text-xl sm:text-2xl italic text-stone-500">{steps.subtitle}</p>
                )}
                <p className="mt-6 text-base sm:text-lg text-stone-700 leading-relaxed text-pretty">
                    {steps.intro}
                </p>
            </div>

            <ol className="mt-12 grid gap-px bg-stone-200 border border-stone-200 sm:grid-cols-2">
                {steps.items.map((item, i) => (
                    <li key={item.title} className="bg-stone-50 p-7 sm:p-9">
                        {/* Tom escuro da própria paleta (Deep Wood) — o ouro
                            #C5A27D sobre fundo claro fica em ~2,3:1 e reprova. */}
                        <span className="font-serif text-4xl text-vanguard-accent leading-none">
                            {String(i + 1).padStart(2, '0')}
                        </span>
                        <h3 className="mt-4 font-serif text-xl sm:text-2xl text-stone-900 leading-snug text-balance">
                            {item.title}
                        </h3>
                        <p className="mt-3 text-base text-stone-700 leading-relaxed text-pretty">{item.text}</p>
                    </li>
                ))}
            </ol>
        </div>
    </section>
);

/* ─────────────────────── CTA no meio ──────────────────────── */

const MidCta = ({ data }: { data: LandingPageData }) => (
    <section className="bg-pacces-ebony text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <p className="font-serif text-2xl sm:text-3xl leading-snug max-w-xl text-balance">
                {data.closing.text}
            </p>
            <WhatsAppCta
                message={data.whatsappMessage}
                label={data.closing.buttonLabel}
                variant="primary"
            />
        </div>
    </section>
);

/* ────────────────────── Checklist docs ────────────────────── */

const Checklist = ({ checklist }: { checklist: NonNullable<LandingPageData['checklist']> }) => (
    <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <div className="max-w-3xl">
            <SectionTitle>{checklist.title}</SectionTitle>
            {checklist.intro && (
                <p className="mt-6 text-base sm:text-lg text-stone-700 leading-relaxed text-pretty">
                    {checklist.intro}
                </p>
            )}
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-2">
            {checklist.groups.map((group, gi) => (
                <div key={gi} className="border border-stone-200 bg-stone-50 p-7 sm:p-8">
                    {group.label && (
                        <h3 className="font-serif text-lg sm:text-xl text-stone-900 mb-5 pb-4 border-b border-stone-200">
                            {group.label}
                        </h3>
                    )}
                    {group.sublist ? (
                        <ol className="space-y-3 list-decimal list-outside pl-5 marker:text-stone-500 marker:font-semibold">
                            {group.items.map((item, i) => (
                                <li key={i} className="text-base text-stone-700 leading-relaxed pl-1">
                                    {item}
                                </li>
                            ))}
                        </ol>
                    ) : (
                        <ul className="space-y-3">
                            {group.items.map((item, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span
                                        aria-hidden="true"
                                        className="material-icons text-vanguard-accent text-base mt-0.5 shrink-0"
                                    >
                                        check
                                    </span>
                                    <span className="text-base text-stone-700 leading-relaxed">{item}</span>
                                </li>
                            ))}
                        </ul>
                    )}

                    {group.sublist && (
                        <ol className="mt-4 ml-5 space-y-2 list-[lower-alpha] list-outside pl-5 marker:text-stone-500">
                            {group.sublist.map((item, i) => (
                                <li key={i} className="text-base text-stone-700 leading-relaxed pl-1">
                                    {item}
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            ))}
        </div>

        {checklist.note && (
            <p className="mt-8 max-w-3xl text-sm text-stone-600 leading-relaxed border-t border-stone-200 pt-6">
                {checklist.note}
            </p>
        )}
    </section>
);

/* ─────────────────────────── FAQ ──────────────────────────── */

const Faq = ({ faq }: { faq: LandingPageData['faq'] }) => (
    <section className="bg-stone-50 border-y border-stone-200">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
            <SectionTitle>{faq.title}</SectionTitle>
            <div className="mt-10 divide-y divide-stone-200 border-t border-stone-200">
                {faq.items.map((item) => (
                    <details key={item.question} className="group py-5">
                        <summary className="flex items-start justify-between gap-5 cursor-pointer list-none min-h-[44px]">
                            <h3 className="font-serif text-lg sm:text-xl text-stone-900 leading-snug text-balance">
                                {item.question}
                            </h3>
                            <span
                                aria-hidden="true"
                                className="material-icons text-stone-500 shrink-0 transition-transform duration-300 group-open:rotate-45 mt-0.5"
                            >
                                add
                            </span>
                        </summary>
                        <p className="mt-4 text-base text-stone-700 leading-relaxed text-pretty">{item.answer}</p>
                    </details>
                ))}
            </div>
        </div>
    </section>
);

/* ───────────────────── Contato / formulário ───────────────── */

const ContactSection = ({ data }: { data: LandingPageData }) => (
    <section id="contato" className="bg-pacces-ebony text-white scroll-mt-6">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid gap-12 lg:grid-cols-2 lg:gap-20">
            <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-pacces-gold">
                    Atendimento Confidencial
                </p>
                <h2 className="mt-5 font-serif text-3xl sm:text-4xl leading-tight text-balance">
                    Fale com um especialista sobre o seu caso
                </h2>
                <p className="mt-5 text-base sm:text-lg text-stone-300 leading-relaxed max-w-md">
                    Envie seus dados e retornaremos pelo WhatsApp. Suas informações são tratadas com total
                    sigilo e confidencialidade.
                </p>

                <div className="mt-9 space-y-4 text-base">
                    <a
                        href={`tel:${PHONE_TEL}`}
                        onClick={() => trackConversion('sp', 'phone')}
                        className="flex items-center gap-3 text-stone-300 hover:text-pacces-gold transition-colors min-h-[44px]"
                    >
                        <span aria-hidden="true" className="material-icons text-pacces-gold text-xl">call</span>
                        {PHONE_DISPLAY}
                    </a>
                    <p className="flex items-center gap-3 text-stone-300">
                        <span aria-hidden="true" className="material-icons text-pacces-gold text-xl">chat</span>
                        WhatsApp {WHATSAPP_DISPLAY}
                    </p>
                    <p className="flex items-start gap-3 text-stone-300">
                        <span aria-hidden="true" className="material-icons text-pacces-gold text-xl">place</span>
                        Av. Ordem e Progresso, 157, Sala 408
                        <br />
                        Barra Funda — São Paulo/SP
                    </p>
                </div>
            </div>

            <LeadForm data={data} />
        </div>
    </section>
);

const INITIAL = { name: '', phone: '', email: '', message: '' };

const LeadForm = ({ data }: { data: LandingPageData }) => {
    const [form, setForm] = useState(INITIAL);
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [waUrl, setWaUrl] = useState('');

    const field = (key: keyof typeof INITIAL, value: string) =>
        setForm((prev) => ({ ...prev, [key]: value }));

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const lines = [
            `Olá! ${data.whatsappMessage}`,
            '',
            `*Nome:* ${form.name}`,
            `*Telefone:* ${form.phone}`,
            `*E-mail:* ${form.email}`,
            `*Área:* ${data.areaLabel}`,
            form.message ? `*Resumo:* ${form.message}` : null,
        ]
            .filter(Boolean)
            .join('\n');

        const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines)}`;
        setWaUrl(url);
        // generate_lead é A conversão do formulário — não disparar conversion_click junto.
        trackEvent('generate_lead', {
            form_id: data.formId,
            area: data.areaLabel,
            ...getAttributionParams(),
        });
        const win = window.open(url, '_blank', 'noopener,noreferrer');
        // Nunca declarar sucesso sem confirmar que o WhatsApp abriu.
        setStatus(win ? 'success' : 'error');
    };

    if (status === 'success') {
        return (
            <div className="bg-stone-900 border border-stone-800 p-8 sm:p-10 flex flex-col justify-center">
                <span aria-hidden="true" className="material-icons text-pacces-gold text-4xl mb-4">
                    check_circle
                </span>
                <h3 className="font-serif text-2xl text-white">WhatsApp aberto</h3>
                <p className="mt-3 text-base text-stone-300 leading-relaxed">
                    Sua mensagem foi preparada — confirme o envio no WhatsApp. Se não abriu, ligue para{' '}
                    {WHATSAPP_DISPLAY}.
                </p>
                <button
                    type="button"
                    onClick={() => setStatus('idle')}
                    className="mt-7 self-start text-pacces-gold text-xs font-bold uppercase tracking-[0.2em] border-b border-pacces-gold/40 hover:border-pacces-gold pb-1 transition-colors min-h-[44px]"
                >
                    Enviar nova mensagem
                </button>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="bg-stone-900 border border-stone-800 p-8 sm:p-10 flex flex-col justify-center">
                <span aria-hidden="true" className="material-icons text-pacces-gold text-4xl mb-4">
                    chat
                </span>
                <h3 className="font-serif text-2xl text-white">Falta um passo</h3>
                <p className="mt-3 text-base text-stone-300 leading-relaxed">
                    Não conseguimos abrir o WhatsApp automaticamente. Toque no botão abaixo para enviar sua
                    mensagem, ou ligue para {WHATSAPP_DISPLAY}.
                </p>
                <a
                    href={waUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackConversion('sp', 'whatsapp')}
                    className="mt-7 inline-flex items-center justify-center gap-2 bg-pacces-gold text-pacces-ebony font-bold px-8 py-4 text-xs uppercase tracking-[0.2em] hover:bg-white transition-colors min-h-[52px]"
                >
                    <span aria-hidden="true" className="material-icons text-base">chat</span>
                    Abrir o WhatsApp
                </a>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="bg-stone-900 border border-stone-800 p-7 sm:p-9 space-y-5">
            <Field
                id={`${data.formId}-name`}
                label="Nome completo"
                required
                value={form.name}
                onChange={(v) => field('name', v)}
                placeholder="Seu nome"
                type="text"
                autoComplete="name"
            />
            <Field
                id={`${data.formId}-phone`}
                label="Telefone / WhatsApp"
                required
                value={form.phone}
                onChange={(v) => field('phone', v)}
                placeholder="(11) 9xxxx-xxxx"
                type="tel"
                autoComplete="tel"
            />
            <Field
                id={`${data.formId}-email`}
                label="E-mail"
                required
                value={form.email}
                onChange={(v) => field('email', v)}
                placeholder="seu@email.com"
                type="email"
                autoComplete="email"
            />

            <div>
                <label
                    htmlFor={`${data.formId}-message`}
                    className="block text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400 mb-2"
                >
                    Resumo do caso <span className="text-stone-500 normal-case tracking-normal">(opcional)</span>
                </label>
                <textarea
                    id={`${data.formId}-message`}
                    rows={3}
                    placeholder="Descreva brevemente sua situação..."
                    value={form.message}
                    onChange={(e) => field('message', e.target.value)}
                    className="w-full bg-pacces-ebony border border-stone-700 text-white text-base px-4 py-3 outline-none resize-none focus:border-pacces-gold transition-colors placeholder:text-stone-500"
                />
            </div>

            <button
                type="submit"
                className="w-full bg-pacces-gold text-pacces-ebony font-bold py-5 text-xs tracking-[0.25em] uppercase hover:bg-white transition-colors flex items-center justify-center gap-3 min-h-[52px]"
            >
                <span aria-hidden="true" className="material-icons text-base">send</span>
                Iniciar Atendimento
            </button>

            <p className="text-xs text-stone-400 text-center leading-relaxed">
                Suas informações são tratadas com total sigilo e confidencialidade.
            </p>
        </form>
    );
};

const Field = ({
    id,
    label,
    value,
    onChange,
    placeholder,
    type,
    required,
    autoComplete,
}: {
    id: string;
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder: string;
    type: string;
    required?: boolean;
    autoComplete?: string;
}) => (
    <div>
        <label htmlFor={id} className="block text-[11px] font-bold uppercase tracking-[0.2em] text-stone-400 mb-2">
            {label} {required && <span className="text-pacces-gold">*</span>}
        </label>
        <input
            id={id}
            type={type}
            required={required}
            autoComplete={autoComplete}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full bg-pacces-ebony border border-stone-700 text-white text-base px-4 py-3 outline-none focus:border-pacces-gold transition-colors placeholder:text-stone-500"
        />
    </div>
);

/* ────────────────────── Peças auxiliares ──────────────────── */

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <h2 className="font-serif text-2xl sm:text-4xl text-stone-900 leading-tight tracking-[-0.02em] text-balance">
        {children}
    </h2>
);

const WhatsAppCta = ({
    message,
    label,
    variant,
}: {
    message: string;
    label: string;
    variant: 'primary' | 'ghost';
}) => {
    // A mensagem contém APENAS o texto do cliente. Nada de metadado de origem:
    // ver o comentário sobre o carimbo removido em attribution.ts.
    const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    const base =
        'inline-flex items-center justify-center gap-3 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors min-h-[52px] text-center';
    const styles =
        variant === 'primary'
            ? 'bg-pacces-gold text-pacces-ebony hover:bg-white'
            : 'border border-stone-600 text-white hover:border-pacces-gold hover:text-pacces-gold';

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackConversion('sp', 'whatsapp')}
            className={`${base} ${styles}`}
        >
            <span aria-hidden="true" className="material-icons text-base">chat</span>
            {label}
        </a>
    );
};

const MinimalFooter = () => (
    <footer className="bg-pacces-ebony border-t border-stone-800 text-stone-400">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <img
                    src="/logo/Logo-Pacces-Advogados-Branco.png"
                    alt="Pacces Advogados"
                    className="h-10 w-auto"
                />
                <div className="text-xs leading-relaxed">
                    {/* Identificação do responsável — exigência de publicidade advocatícia
                        (Prov. 205/2021). Apenas nas LPs de anúncio, por decisão do cliente. */}
                    <p className="text-stone-300 font-medium">OAB/SP 402.384</p>
                    <p className="mt-1">Av. Ordem e Progresso, 157, Sala 408 — Barra Funda, São Paulo/SP</p>
                    <p className="mt-1">
                        {PHONE_DISPLAY} ·{' '}
                        <a
                            href="mailto:contato@paccesadvogados.com.br"
                            onClick={() => trackConversion('sp', 'email')}
                            className="inline-block py-2 hover:text-pacces-gold transition-colors"
                        >
                            contato@paccesadvogados.com.br
                        </a>
                    </p>
                    <p className="mt-1">
                        <a
                            href="/privacidade"
                            className="inline-block py-2 hover:text-pacces-gold transition-colors"
                        >
                            Política de Privacidade
                        </a>
                    </p>
                </div>
            </div>

            <div className="mt-8 pt-6 border-t border-stone-800 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-4">
                <p className="text-center sm:text-left text-[11px] text-stone-500 uppercase tracking-[0.2em] font-medium">
                    © {new Date().getFullYear()} Pacces Advogados
                </p>
                <a
                    href="https://devapex.us"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="justify-self-center flex items-center gap-3 whitespace-nowrap opacity-70 hover:opacity-100 transition-opacity"
                >
                    <span className="text-[11px] text-stone-400 uppercase tracking-[0.2em] font-medium">
                        Developed by
                    </span>
                    <img src="/apexdev_linear_white.svg" alt="ApexDev" className="h-4 w-auto" />
                </a>
            </div>
        </div>
    </footer>
);
